import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useUserStore } from '../stores/useUserStore';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Save,
  Trash2,
  Tag,
  FileText,
  CheckCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import CostService from '../services/costService';
import { nekoPos } from 'public-contracts';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

type CostCategory = nekoPos.CostCategory;

interface CostCategoryFormData {
  name: string;
  description: string;
  parent: string;
  isDefault: boolean;
}

const ModernCostCategoryEdit: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { user, fetchUser } = useUserStore();
  const { id } = useParams<{ id: string }>();
  const costService = useMemo(() => new CostService(), []);

  const [category, setCategory] = useState<CostCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parentCategories, setParentCategories] = useState<CostCategory[]>([]);

  const methods = useForm<CostCategoryFormData>({
    defaultValues: {
      name: '',
      description: '',
      parent: '',
      isDefault: false,
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const hasPermission = (action: 'list' | 'create' | 'edit' | 'delete') => {
    // If user is not loaded yet, allow basic operations
    if (!user) {
      return ['list', 'create', 'edit'].includes(action);
    }
    if (user.roles?.includes('sysAdmin')) return true;
    if (user.roles?.includes('admin')) return true;
    if (user.roles?.includes('staff')) return true;
    if (user.roles?.includes('storeManager')) return true;
    if (user.roles?.includes('cashier')) {
      return action !== 'delete';
    }

    // If user has no roles or empty roles, allow basic operations
    if (!user.roles || user.roles.length === 0) {
      return ['list', 'create', 'edit'].includes(action);
    }

    return false;
  };

  // Load parent categories for dropdown
  useEffect(() => {
    const loadParentCategories = async () => {
      try {
        const categoriesData = await costService.getCostCategories({
          perPage: 1000, // Get all categories for the dropdown
        });
        const categoriesArray = Array.isArray(categoriesData)
          ? categoriesData
          : [];
        setParentCategories(categoriesArray);
      } catch (error) {
        console.error('Error loading parent categories:', error);
        setParentCategories([]);
      }
    };
    loadParentCategories();
  }, [costService]);

  // Load category data
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const categoryData = await costService.getCostCategory(id);
        setCategory(categoryData);
        setValue('name', categoryData.name);
        setValue('description', categoryData.description);
        setValue('parent', categoryData.parent || '');
        setValue('isDefault', categoryData.isDefault);
      } catch (error) {
        console.error('Error loading category data:', error);
        alert(translate('pos.cost_categories.load_error'));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, setValue, costService, translate]);

  if (!hasPermission('edit')) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {translate('pos.common.accessDenied')}
          </p>
          <p className="text-muted-foreground">
            {translate('pos.cost_categories.no_permission_edit')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <LoadingSpinner size="lg" variant="primary" className="mx-auto mb-4" />
          <p className="text-muted-foreground">
            {translate('pos.common.loadingCategory')}
          </p>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {translate('pos.common.categoryNotFound')}
          </p>
          <Button onClick={() => navigate('/cost_categories')}>
            {translate('pos.common.backToCategories')}
          </Button>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: CostCategoryFormData) => {
    setSaving(true);
    try {
      const categoryData: Partial<CostCategory> = {
        name: data.name,
        description: data.description,
        parent: data.parent || '',
        isDefault: data.isDefault,
      };
      await costService.updateCostCategory(id!, categoryData);
      navigate('/cost_categories');
    } catch (error) {
      console.error('Error updating cost category:', error);
      alert(translate('pos.cost_categories.update_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!hasPermission('delete')) {
      alert(translate('pos.cost_categories.no_permission_delete'));
      return;
    }

    if (
      window.confirm(
        translate('pos.cost_categories.delete_confirm_irreversible')
      )
    ) {
      try {
        await costService.deleteCostCategory(id!);
        navigate('/cost_categories');
      } catch (error) {
        console.error('Error deleting category:', error);
        alert(translate('pos.cost_categories.delete_error'));
      }
    }
  };

  const handleCancel = () => {
    navigate('/cost_categories');
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          className="border-border text-foreground hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {translate('pos.common.action.back')}
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.cost_categories.edit_title')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate('pos.cost_categories.edit_subtitle')}
          </p>
        </div>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    {translate('pos.cost_categories.basic_information')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium">
                      {translate('pos.costCategories.fields.name')} *
                    </Label>
                    <Input
                      id="name"
                      placeholder={translate(
                        'pos.cost_categories.name_placeholder'
                      )}
                      {...register('name', {
                        required: translate(
                          'pos.cost_categories.name_required'
                        ),
                      })}
                      className="mt-1"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label
                      htmlFor="description"
                      className="text-sm font-medium"
                    >
                      {translate('pos.costCategories.fields.description')} *
                    </Label>
                    <Textarea
                      id="description"
                      placeholder={translate(
                        'pos.cost_categories.description_placeholder'
                      )}
                      {...register('description', {
                        required: translate(
                          'pos.cost_categories.description_required'
                        ),
                      })}
                      className="mt-1"
                      rows={4}
                    />
                    {errors.description && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.description.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="parent" className="text-sm font-medium">
                      {translate('pos.costCategories.fields.parent')}
                    </Label>
                    <Select
                      value={watch('parent') || ''}
                      onValueChange={(value) =>
                        setValue('parent', value === 'none' ? '' : value)
                      }
                    >
                      <SelectTrigger id="parent" className="mt-1">
                        <SelectValue
                          placeholder={translate(
                            'pos.cost_categories.parent_placeholder',
                            'Select parent category (optional)'
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          {translate('pos.cost_categories.none', 'None')}
                        </SelectItem>
                        {parentCategories
                          .filter((cat) => cat.id !== id) // Prevent circular reference
                          .map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isDefault"
                      {...register('isDefault')}
                      className="rounded border-border"
                    />
                    <Label htmlFor="isDefault" className="text-sm font-medium">
                      {translate('pos.cost_categories.set_as_default')}
                    </Label>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-4 space-y-4">
                {/* Summary Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {translate('pos.cost_categories.summary')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {translate('pos.costCategories.fields.name')}:
                      </span>
                      <span className="font-medium">
                        {watch('name') ||
                          translate('pos.cost_categories.not_entered')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {translate('pos.costCategories.fields.parent')}:
                      </span>
                      <span className="font-medium">
                        {watch('parent')
                          ? parentCategories.find(
                              (cat) => cat.id === watch('parent')
                            )?.name || watch('parent')
                          : translate('pos.cost_categories.none')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {translate('pos.costCategories.fields.isDefault')}:
                      </span>
                      <span className="font-medium">
                        {watch('isDefault')
                          ? translate('pos.cost_categories.yes')
                          : translate('pos.cost_categories.no')}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Actions */}
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <Button type="submit" className="w-full" disabled={saving}>
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {translate('common.saving')}
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {translate('pos.cost_categories.update_category')}
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      className="w-full border-border text-foreground hover:bg-muted"
                    >
                      {translate('pos.common.action.cancel')}
                    </Button>
                    {hasPermission('delete') && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDelete}
                        className="w-full border-border text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {translate('pos.cost_categories.delete_category')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ModernCostCategoryEdit;
